// The Spread Fertilizer panel read the cache and never reconciled: `spreadRx.
import { useEffect, useRef, useState } from "react";
import { hydrateFertRx } from "../lib/fertSets";
import { useRecSetsBus } from "../stores/recSetsBus";

/*
 *
 * Reconcile every given field's saved Rx from the server, once per field per mount-cycle.
 *
 * Returns `hydrating` — true until every field has answered. Callers MUST use it to suppress
 * "no saved Rx" copy: saying "none" before the server has been asked is the bug itself, one layer up.
 * Also returns the rec-sets bus `version`, so a caller can put it in a memo/query key and recompute
 * when the reconciled sets land (the spread family reads localStorage synchronously and would
 * otherwise keep serving the pre-hydration snapshot for the life of the panel).
 */
export function useHydratedRecSets(fieldIds: string[]): {
  hydrating: boolean;
  version: number;
} {
  const version = useRecSetsBus((s) => s.version);
  const [pending, setPending] = useState(0);
  // Per-field dedup across re-renders: the panel re-renders on every bus bump, and re-hydrating on
  // each would loop (hydrate bumps the bus). Keyed by field id, not by array identity, so adding a
  // field to the selection hydrates only the new one.
  const done = useRef<Set<string>>(new Set());
  const key = fieldIds.join(",");

  useEffect(() => {
    // `key` is the field-id list. Reading `fieldIds` here would re-hydrate on array identity.
    const ids = key ? key.split(",") : [];
    const todo = ids.filter((id) => id && !done.current.has(id));
    if (!todo.length) return;
    todo.forEach((id) => done.current.add(id));
    setPending((n) => n + todo.length);
    for (const id of todo) {
      // The effect's cleanup runs on a plain dep change (adding a field to the selection) while the
      // component is still MOUNTED, and gating the decrement there leaked `pending`: the in-flight
      // field's finally never ran it down, so `hydrating` stuck true — both surfaces read "Reading
      // saved Rx…" forever — until remount.
      void hydrateFertRx(id).finally(() =>
        setPending((n) => Math.max(0, n - 1)),
      );
    }
  }, [key]);

  return { hydrating: pending > 0, version };
}
